/**
 * Typed callers for /api/v1/b2b-invoices (BP-09, plan 11 Task 8). Corporate / B2B
 * invoices, each coupled to a fiscal_receipts row with doc_type='e_invoice'. Used
 * by the admin-web billing UI (B2BInvoiceForm/List/Detail). owner|manager auth;
 * void is owner-only. The same idempotency_key makes a retried create exactly-once.
 */
import { ApiError, type ErrorEnvelope, isErrorCode, type schemas } from '@gym/shared';

import type { GymSupabaseClient } from '../supabase.js';

export interface CreateB2BInvoiceResult {
  id: string;
  status: 'issued' | 'draft';
  fiscal_status: 'issued' | 'failed_retriable' | 'failed' | 'pending';
  invoice_number: string | null;
  fiscal_receipt_id: string | null;
  subtotal?: number;
  tax_total?: number;
  total?: number;
  currency?: 'MKD';
  retriable?: boolean;
}

export interface B2BInvoiceView {
  id: string;
  invoice_number: string | null;
  customer_company: string;
  customer_tax_id: string;
  customer_email: string | null;
  subtotal: number;
  tax_total: number;
  total: number;
  currency: 'MKD';
  status: 'draft' | 'issued' | 'sent' | 'voided';
  fiscal_status: 'pending' | 'issued' | 'failed_retriable' | 'failed' | null;
  fiscal_receipt_id: string | null;
  issued_at: string | null;
  sent_at: string | null;
  created_at: string;
}

export interface B2BInvoiceListFilter {
  status?: 'draft' | 'issued' | 'sent' | 'voided';
  customer_tax_id?: string;
  from?: string; // ISO date, issued_at >=
  to?: string; // ISO date, issued_at <=
  limit?: number;
}

function unwrap<T>(data: T | ErrorEnvelope | null, error: { message: string } | null): T {
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  if (data && typeof data === 'object' && 'error' in data && (data as ErrorEnvelope).error) {
    const env = (data as ErrorEnvelope).error;
    const code = isErrorCode(env.code) ? env.code : 'internal_error';
    throw new ApiError(code, {
      message: env.message,
      ...(env.details !== undefined ? { details: env.details } : {}),
      requestId: env.request_id,
      retriable: env.retriable,
    });
  }
  return data as T;
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export async function createB2BInvoice(
  client: GymSupabaseClient,
  req: schemas.B2BInvoiceRequest,
): Promise<CreateB2BInvoiceResult> {
  const { data, error } = await client.functions.invoke<CreateB2BInvoiceResult | ErrorEnvelope>(
    'b2b-invoices',
    {
      headers: { 'Idempotency-Key': req.idempotency_key },
      body: req,
    },
  );
  return unwrap(data, error);
}

export async function getB2BInvoice(
  client: GymSupabaseClient,
  id: string,
): Promise<B2BInvoiceView> {
  const { data, error } = await client.functions.invoke<B2BInvoiceView | ErrorEnvelope>(
    `b2b-invoices/${id}`,
    { method: 'GET' },
  );
  return unwrap(data, error);
}

export async function listB2BInvoices(
  client: GymSupabaseClient,
  filter: B2BInvoiceListFilter = {},
): Promise<{ invoices: B2BInvoiceView[] }> {
  const params = new URLSearchParams();
  if (filter.status) params.set('status', filter.status);
  if (filter.customer_tax_id) params.set('customer_tax_id', filter.customer_tax_id);
  if (filter.from) params.set('from', filter.from);
  if (filter.to) params.set('to', filter.to);
  if (filter.limit != null) params.set('limit', String(filter.limit));
  const qs = params.toString();
  const { data, error } = await client.functions.invoke<
    { invoices: B2BInvoiceView[] } | ErrorEnvelope
  >(`b2b-invoices${qs ? `?${qs}` : ''}`, { method: 'GET' });
  return unwrap(data, error);
}

export async function sendB2BInvoice(
  client: GymSupabaseClient,
  id: string,
): Promise<{ id: string; status: 'sent'; sent_at?: string }> {
  const { data, error } = await client.functions.invoke<
    { id: string; status: 'sent'; sent_at?: string } | ErrorEnvelope
  >(`b2b-invoices/${id}/send`, { method: 'POST', body: {} });
  return unwrap(data, error);
}

export async function voidB2BInvoice(
  client: GymSupabaseClient,
  id: string,
): Promise<{ id: string; status: 'voided' }> {
  const { data, error } = await client.functions.invoke<
    { id: string; status: 'voided' } | ErrorEnvelope
  >(`b2b-invoices/${id}/void`, { method: 'POST', body: {} });
  return unwrap(data, error);
}
